// V2LoginFeature.js
// Main container for the V2 login page. Manages view toggle (login/signup),
// Okta modal visibility, and wraps V2ThemeProvider for theme support.
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { V2ThemeProvider } from '../../themecontrols/v2_ThemeContext';
import httpClient from '../../../httpClient';
import useV2AuthForm from './hooks/useV2AuthForm';
import useV2OktaExchange from './hooks/useV2OktaExchange';
import { buildOktaRedirectUrl, openForgotPasswordPage } from './services/v2AuthService';
import '../../themecontrols/themecontrol.css';
import V2LoginCard from './components/V2LoginCard';
import V2SignupCard from './components/V2SignupCard';
import V2OktaModal from './components/V2OktaModal';
import './V2LoginFeature.css';

const SIGNUP_ROUTE = '/signup';
const LOGIN_ROUTE = '/login';

/**
 * V2LoginFeature — top-level route component for /login and /signup in App_v2.js.
 * Renders login or signup card, handles Okta SSO modal, and exchanges
 * Okta callback codes on mount.
 */
function V2LoginFeature() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── View toggle state ────────────────────────────────────────────────────
  // The URL mirrors the view: /signup shows the signup card, /login the login card. Deep links
  // (share/invite emails) open the right card directly, and the toggle below updates the URL.
  const isSignupRoute = location.pathname === SIGNUP_ROUTE;
  const [isSignUp, setIsSignUp] = useState(isSignupRoute);

  // Keep the card in sync when the route changes (browser back/forward). Other paths ('/') leave it alone.
  useEffect(() => {
    if (location.pathname === SIGNUP_ROUTE) setIsSignUp(true);
    else if (location.pathname === LOGIN_ROUTE) setIsSignUp(false);
  }, [location.pathname]);
  const [showOktaModal, setShowOktaModal] = useState(false);
  const [oktaPurpose, setOktaPurpose] = useState('login');

  /**
   * Called after successful email/password login or Okta exchange.
   * Checks the org ui_v2 feature flag, then navigates back to the page the user
   * originally tried to reach (captured by V2PrivateRoute as ?redirect=) — falling
   * back to Cloo AI home (org AI agent home) when there's no redirect target.
   * Only a same-origin relative path (single leading "/") is honored, to avoid
   * an open redirect via a crafted "//evil.com" or absolute URL query value.
   */
  const handleLoginSuccess = useCallback(async () => {
    const redirectParam = new URLSearchParams(location.search).get('redirect');
    const redirectTarget = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
      ? redirectParam
      : '/v2/discovery/ai-agent-home';
    try {
      const token = localStorage.getItem('ids_token');
      const response = await httpClient.post(
        '/ids/check_ui_v2_enabled',
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      let payload = response.data?.data;
      if (payload && typeof payload === 'object' && payload.data) {
        payload = payload.data;
      }
      const isV2Enabled = Boolean(
        payload && typeof payload.is_ui_v2_enabled === 'boolean' && payload.is_ui_v2_enabled
      );
      console.log('[V2LoginFeature] ui_v2 flag:', { isV2Enabled });
      navigate(redirectTarget);
    } catch (err) {
      console.error('[V2LoginFeature] flag check failed, defaulting to Cloo AI', err);
      navigate(redirectTarget);
    }
  }, [navigate, location.search]);

  // ── Auth form hook ───────────────────────────────────────────────────────
  const formProps = useV2AuthForm({
    onLoginSuccess: handleLoginSuccess,
  });

  const { clearForm, setPhoneError } = formProps;

  // ── Okta exchange hook (runs on mount if Okta callback detected) ─────────
  const { exchangeLoading, exchangeError } = useV2OktaExchange({
    onSuccess: handleLoginSuccess,
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  /**
   * Opens Okta modal. Called by Login/Signup card Okta button.
   * @param {'login'|'signup'} purpose
   */
  const handleOktaClick = useCallback((purpose) => {
    setOktaPurpose(purpose);
    setShowOktaModal(true);
  }, []);

  /**
   * Redirects browser to backend Okta endpoint. Called by V2OktaModal on submit.
   * @param {string} email  - Work email validated by modal
   * @param {'login'|'signup'} purpose
   */
  const handleOktaSubmit = useCallback((email, purpose) => {
    const url = buildOktaRedirectUrl(purpose, email);
    window.location.href = url;
  }, []);

  /**
   * Switches to signup view. Clears form. Called by V2LoginCard "Create account" button.
   */
  const handleGoToSignup = useCallback(() => {
    clearForm();
    setIsSignUp(true);
    navigate(SIGNUP_ROUTE);
  }, [clearForm, navigate]);

  /**
   * Switches to login view. Clears form + phoneError. Called by V2SignupCard "Back to login" link.
   */
  const handleGoToLogin = useCallback(() => {
    clearForm();
    setPhoneError('');
    setIsSignUp(false);
    navigate(LOGIN_ROUTE);
  }, [clearForm, setPhoneError, navigate]);

  /**
   * Called after successful signup — flips view back to login.
   */
  const handleSignupSuccess = useCallback(() => {
    setIsSignUp(false);
    navigate(LOGIN_ROUTE, { replace: true });
  }, [navigate]);

  /**
   * Passed to V2SignupCard as onSignup — wraps handleSignup with the success callback.
   */
  const handleSignupSubmit = useCallback(() => {
    formProps.handleSignup(handleSignupSuccess);
  }, [formProps, handleSignupSuccess]);

  console.log('[V2LoginFeature.js] r1: isSignUp=', isSignUp, 'showOktaModal=', showOktaModal);

  return (
    <V2ThemeProvider>
      <div className="v2-login-root">
        <div className="v2-login-page">
          <div className="v2-login-page__layout">

            {/* Marketing panel — visible only >= 1280px */}
            <div className="v2-login-page__marketing">
              <h2 className="v2-login-page__tagline">
                Perfect Alignment,<br />
                From{' '}
                <span className="v2-login-page__brand-text">Discovery</span>
                {' '}To{' '}
                <span className="v2-login-page__brand-text">Delivery</span>.
              </h2>
              <p className="v2-login-page__subtext">
                Capture the why. Map the processes.<br />
                Lock the requirements. Keep transformation aligned.
              </p>
            </div>

            {/* Auth card column */}
            <div className="v2-login-page__card-wrap">
              {/* Exchange loading overlay — shown during Okta code exchange */}
              {exchangeLoading && (
                <div className="v2-login-page__exchange-loading">
                  <span>Signing you in with Okta...</span>
                </div>
              )}

              {/* Exchange error banner */}
              {exchangeError && (
                <div className="v2-login-page__exchange-error">{exchangeError}</div>
              )}

              {/* Auth card — switches between login and signup */}
              {!exchangeLoading && (
                isSignUp
                  ? (
                    <V2SignupCard
                      {...formProps}
                      onBackToLogin={handleGoToLogin}
                      onOktaClick={handleOktaClick}
                      onSignup={handleSignupSubmit}
                    />
                  )
                  : (
                    <V2LoginCard
                      {...formProps}
                      onCreateAccount={handleGoToSignup}
                      onOktaClick={handleOktaClick}
                      onForgotPassword={openForgotPasswordPage}
                    />
                  )
              )}
            </div>
          </div>
        </div>

        {/* Footer — visible below the 100vh login area on scroll */}
        <footer className="v2-login-footer">
          <div className="v2-login-footer__links">
            <a
              href="https://cloobot-public.s3.ap-south-1.amazonaws.com/Cloobot+Product+Suite+Terms+and+Conditions.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="v2-login-footer__link"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="https://cloobot-public.s3.ap-south-1.amazonaws.com/Cloobot+Suite+Privacy+Policy.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="v2-login-footer__link"
            >
              Privacy Policy
            </a>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfFKpduoF3PnPmDeoNRdgLEVZpbfFsdJ-Ufj9hFVOz3-cC_8A/viewform?usp=dialog"
              target="_blank"
              rel="noopener noreferrer"
              className="v2-login-footer__link"
            >
              Contact Support
            </a>
            <a
              href="https://www.cloobot.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="v2-login-footer__link v2-login-footer__link--brand"
            >
              &copy; Cloobot Inc.
            </a>
          </div>
        </footer>

        {/* Okta modal — outside .v2-login-page so position:fixed is not overridden */}
        <V2OktaModal
          isOpen={showOktaModal}
          purpose={oktaPurpose}
          onClose={() => setShowOktaModal(false)}
          onSubmit={handleOktaSubmit}
        />
      </div>
    </V2ThemeProvider>
  );
}

export default V2LoginFeature;
